import {
  type CellContext,
  columnFilteringFeature,
  columnSizingFeature,
  columnVisibilityFeature,
  createFilteredRowModel,
  createSortedRowModel,
  globalFilteringFeature,
  rowSortingFeature,
  sortFn_alphanumeric,
  sortFn_datetime,
  sortFn_text,
  tableFeatures,
} from '@tanstack/react-table';
import type { ReactNode } from 'react';

import type { OrderDirection } from '@repo/frontend-shared';

export type SDSTableAlign = 'left' | 'center' | 'right';

export const SDS_TABLE_FEATURES = tableFeatures({
  columnFilteringFeature,
  globalFilteringFeature,
  rowSortingFeature,
  columnVisibilityFeature,
  columnSizingFeature,
  filteredRowModel: createFilteredRowModel(),
  sortedRowModel: createSortedRowModel(),
  sortFns: {
    alphanumeric: sortFn_alphanumeric,
    datetime: sortFn_datetime,
    text: sortFn_text,
  },
});

export type SDSTableFeatures = typeof SDS_TABLE_FEATURES;

interface BaseTableColumn {
  id: string;
  label: string;
  sortable?: boolean;
  filterable?: boolean;
  width?: string;
  align?: SDSTableAlign;
  dataTestId?: string;
  cellDataTestId?: string;
}

export type TableColumn<T extends TableData> = BaseTableColumn &
  (
    | {
        render: (row: T) => ReactNode;
        renderCell?: never;
      }
    | {
        render?: never;
        renderCell: (cell: CellContext<SDSTableFeatures, T, unknown>) => ReactNode;
      }
  );

export interface TableData {
  id: string;
}

export interface TableProps<T extends TableData> {
  columns: TableColumn<T>[];
  data: T[];
  sortable?: boolean;
  filterable?: boolean;
  virtualized?: boolean;
  rowHeight?: number;
  onRowClick?: (row: T) => void;
  onLoadMore?: () => void;
  paginationKey?: string;
  hasMore?: boolean;
  isLoading?: boolean;
  className?: string;
  dataTestId?: string;
  selectable?: boolean;
  selectedIds?: string[];
  onSelectionChange?: (selectedIds: string[]) => void;
  getRowId?: (row: T) => string;
  sortField?: string;
  sortDirection?: OrderDirection;
  onSort?: (field: string) => void;
  renderActions?: (row: T) => ReactNode;
  actionsLabel?: string;
  actionsHeaderDataTestId?: string;
  /** When set with renderExpandedRow, renders an accordion panel under that row. */
  expandedRowId?: string | null;
  renderExpandedRow?: (row: T) => ReactNode;
}

export interface SortState {
  key: string;
  direction: OrderDirection;
}

export interface FilterState {
  key: string;
  value: string;
}

export interface InfiniteScrollState {
  hasMore: boolean;
  isLoading: boolean;
  loadedCount: number;
}
